import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronDown, ArrowRight, Briefcase, Calendar, Sun, Home, Sparkles } from 'lucide-react';
import { navigationLinks, contactInfo } from '../data';
import Logo from './Logo';

const serviceIcons = {
  Briefcase,
  Calendar,
  Sun,
  Home,
  Sparkles
};

export default function Header({ services = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 500); // 500ms delay
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    setIsOpen(false);

    if (href.startsWith('/#')) {
      const hash = href.substring(1); // e.g. "#ydelser"
      if (location.pathname !== '/') {
        navigate(href);
      } else {
        const element = document.querySelector(hash);
        if (element) {
          const offset = 80; // Header height
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    } else if (href === '/') {
      if (location.pathname !== '/') {
        navigate('/');
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(href);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const handleCtaClick = () => {
    setIsOpen(false);
    navigate('/kontakt');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      {/* Main Navigation */}
      <nav className={`transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-3' : 'bg-white py-4'} border-b border-[#0e227b]/10`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <a href="/" onClick={(e) => handleLinkClick(e, '/')} className="flex items-center pl-1 sm:pl-2">
              <Logo className="h-9 sm:h-11" />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigationLinks.map((link) => {
                if (link.label === 'Ydelser') {
                  return (
                    <div
                      key={link.href}
                      className="relative py-2"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setIsDropdownOpen(!isDropdownOpen);
                        }}
                        className="flex items-center gap-1 text-slate-700 hover:text-[#0e227b] font-semibold text-xs uppercase tracking-wider transition-colors focus:outline-none cursor-pointer"
                      >
                        {link.label}
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu */}
                      <div
                        className={`absolute left-1/2 -translate-x-1/2 mt-3 w-64 bg-white rounded-2xl shadow-xl border border-slate-100 py-3 z-50 transition-all duration-200 origin-top ${
                          isDropdownOpen
                            ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                            : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
                        }`}
                      >
                        <div className="space-y-1 px-2">
                          {services.map((service) => {
                            const slug = service.slug._ || service.slug;
                            return (
                              <a
                                key={service.id}
                                href={`/ydelser/${slug}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsDropdownOpen(false);
                                  navigate(`/ydelser/${slug}`);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className="block px-3 py-2 rounded-xl hover:bg-[#0e227b]/5 text-xs font-bold text-slate-900 hover:text-[#0e227b] transition-colors uppercase tracking-tight"
                              >
                                {service.title}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link.href)}
                    className="text-slate-700 hover:text-[#0e227b] font-semibold text-xs uppercase tracking-wider transition-colors relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-0.5 after:bg-[#0e227b] hover:after:w-full after:transition-all after:duration-300"
                  >
                    {link.label}
                  </a>
                );
              })}
              <button
                onClick={handleCtaClick}
                className="bg-[#0e227b] hover:bg-[#0e227b]/90 text-white px-5 py-2.5 rounded-full font-semibold text-sm shadow-sm hover:shadow transition-all duration-200 transform hover:-translate-y-0.5"
              >
                Få gratis tilbud
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-slate-700 hover:text-[#0e227b] p-1 focus:outline-none transition-colors"
                aria-label="Menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Sidebar Overlay */}
      <div
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-50 transition-opacity duration-300 lg:hidden ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsOpen(false)}
      />

      {/* Mobile Menu Sidebar */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-[300px] max-w-[85vw] bg-white z-50 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header with Logo and Close button */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <a href="/" onClick={(e) => handleLinkClick(e, '/')} className="flex items-center">
            <Logo className="h-9" />
          </a>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 text-slate-500 hover:text-[#0e227b] transition-colors focus:outline-none"
            aria-label="Luk menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto p-4 space-y-0.5">
          {navigationLinks.map((link) => {
            if (link.label === 'Ydelser') {
              return (
                <div key={link.href} className="space-y-0.5">
                  <button
                    onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                    className="w-full flex items-center justify-between px-4 py-2 rounded-xl text-sm font-semibold uppercase tracking-wider text-slate-700 hover:text-[#0e227b] hover:bg-[#0e227b]/5 transition-all focus:outline-none cursor-pointer"
                  >
                    <span>{link.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isMobileDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Mobile Dropdown Items */}
                  <div
                    className={`pl-4 space-y-0.5 overflow-hidden transition-all duration-300 ${
                      isMobileDropdownOpen ? 'max-h-[400px] opacity-100 py-0.5' : 'max-h-0 opacity-0'
                    }`}
                  >
                    {services.map((service) => {
                      const slug = service.slug._ || service.slug;
                      return (
                        <a
                          key={service.id}
                          href={`/ydelser/${slug}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setIsOpen(false);
                            setIsMobileDropdownOpen(false);
                            navigate(`/ydelser/${slug}`);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="block px-4 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider text-slate-600 hover:text-[#0e227b] hover:bg-[#0e227b]/5 transition-all"
                        >
                          {service.title}
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.href)}
                className="flex items-center px-4 py-2 rounded-xl text-sm font-semibold uppercase tracking-wider text-slate-700 hover:text-[#0e227b] hover:bg-[#0e227b]/5 transition-all"
              >
                {link.label}
              </a>
            );
          })}
        </div>

        {/* Footer with CTA and contact info */}
        <div className="p-5 border-t border-slate-100 bg-slate-50 space-y-5">
          <button
            onClick={handleCtaClick}
            className="w-full bg-[#0e227b] hover:bg-[#0e227b]/90 text-white text-center py-3 rounded-full font-semibold shadow-sm transition-colors text-sm uppercase tracking-wider"
          >
            Få gratis tilbud
          </button>
          
          {/* Contact info */}
          <div className="space-y-3 text-xs text-slate-500 px-2">
            <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="flex items-center gap-2.5 hover:text-[#0e227b] transition-colors">
              <Phone className="w-4 h-4 text-[#0e227b]" />
              <span className="font-medium">{contactInfo.phone}</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
